import { useState } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { ContactKind, accessErrorMessage, formatContact } from './accessErrors';

const {
    cradle: { munduClient },
} = container;

export const CONTACT_KINDS: ContactKind[] = ['email', 'phone'];

const compact = (value: string) => value.replace(/\s/g, '').toLowerCase();

/**
 * Email and phone of a relative or employee as staff edit them inside the edit form.
 * Confirmed contacts are read-only; the rest are saved together with the form.
 */
export const useStaffLoginContacts = (
    role: 'creators' | 'nextOfKins',
    userId: string,
    organizationId: string,
    /** Staff without edit access do not load contacts. */
    enabled = true,
    /** Shown until contacts load, so the form opens in its final layout. */
    initial: {
        email?: string;
        phone?: string;
        pending?: boolean;
    } = {},
) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [edits, setEdits] = useState<Partial<Record<ContactKind, string>>>(
        {},
    );
    const [errors, setErrors] = useState<Partial<Record<ContactKind, string>>>(
        {},
    );
    const { data, mutate } = useSWR(
        enabled ? ['login-contacts', role, userId, organizationId] : null,
        () => munduClient.getLoginContacts(role, userId, organizationId),
    );

    const current = (kind: ContactKind) =>
        data?.contacts.find(contact => contact.kind === kind);
    const value = (kind: ContactKind) => {
        if (!data) return edits[kind] ?? initial[kind] ?? '';
        const contact = current(kind);
        return edits[kind] ?? (contact ? formatContact(contact) : '');
    };
    // A known contact stays read-only until it is clear whether it is confirmed.
    const isConfirmed = (kind: ContactKind) =>
        data ? Boolean(current(kind)?.verified) : Boolean(initial[kind]);
    const changedKinds = () =>
        CONTACT_KINDS.filter(
            kind =>
                !isConfirmed(kind) &&
                edits[kind] !== undefined &&
                compact(edits[kind] ?? '') !==
                    compact(current(kind)?.value ?? ''),
        );

    const change = (kind: ContactKind, next: string) => {
        setEdits(previous => ({ ...previous, [kind]: next }));
        setErrors(previous => ({ ...previous, [kind]: undefined }));
    };

    const reset = () => {
        setEdits({});
        setErrors({});
    };

    const sendInvitation = async (kind: ContactKind) => {
        const contact = current(kind);
        if (!contact) return;
        try {
            await munduClient.resendAccountInvitation(
                role,
                userId,
                kind,
                organizationId,
            );
            notify(
                t('accountAccess.invitation.sent', {
                    contact: formatContact(contact),
                }),
            );
        } catch (cause) {
            notify(accessErrorMessage(cause, t), true);
        }
    };

    /** Saves changed contacts. Returns false and shows the error on the field when one fails. */
    const save = async (): Promise<boolean> => {
        const kinds = changedKinds();
        for (const kind of kinds) {
            const next = (edits[kind] ?? '').trim();
            try {
                if (next)
                    await munduClient.setLoginContact(
                        role,
                        userId,
                        kind,
                        next,
                        organizationId,
                    );
                else
                    await munduClient.removeLoginContact(
                        role,
                        userId,
                        kind,
                        organizationId,
                    );
            } catch (cause) {
                setErrors(previous => ({
                    ...previous,
                    [kind]: accessErrorMessage(cause, t),
                }));
                return false;
            }
        }
        if (!kinds.length) return true;
        // A corrected contact on an open invitation gets the invitation straight away.
        if (data?.pending)
            for (const kind of kinds)
                if ((edits[kind] ?? '').trim())
                    await munduClient
                        .resendAccountInvitation(
                            role,
                            userId,
                            kind,
                            organizationId,
                        )
                        .catch(() => undefined);
        await mutate();
        setEdits({});
        return true;
    };

    return {
        loaded: Boolean(data),
        pending: data ? Boolean(data.pending) : Boolean(initial.pending),
        hasContact: (kind: ContactKind) =>
            data ? Boolean(current(kind)) : Boolean(initial[kind]),
        value,
        isConfirmed,
        error: (kind: ContactKind) => errors[kind],
        change,
        reset,
        save,
        sendInvitation,
    };
};

export type StaffLoginContacts = ReturnType<typeof useStaffLoginContacts>;
